import type { IGatsbyImageData, Layout } from 'gatsby-plugin-image';
import type { ImageAsset } from '../../schema';

const SCALE_FACTORS = [4, 2, 1] as const;

const maybeProxyUrl = (absoluteUrl: string) => {
  if (typeof window === 'undefined') return absoluteUrl;
  if (process.env.NODE_ENV === 'test') return absoluteUrl;
  const origin = window.location.origin;
  return `${origin}/api/image-proxy?url=${encodeURIComponent(absoluteUrl)}`;
};

const buildContentfulUrl = (
  image: ImageAsset,
  scaleFactor = 1,
  queryParams: Record<string, string> = {},
  maxWidth?: number,
) => {
  const url = new URL(image.url);
  // Apply max width constraint before scaling
  const constrainedWidth = maxWidth
    ? Math.min(image.width, maxWidth)
    : image.width;
  const constrainedHeight = maxWidth
    ? Math.round((constrainedWidth / image.width) * image.height)
    : image.height;

  const width = Math.round(constrainedWidth / scaleFactor);
  const height = Math.round(constrainedHeight / scaleFactor);

  url.search = new URLSearchParams({
    w: width.toString(),
    h: height.toString(),
    q: '80',
    ...queryParams,
  }).toString();

  return [maybeProxyUrl(url.toString()), width] as const;
};

const buildSrcSet = (
  image: ImageAsset,
  queryParams: Record<string, string> = {},
  maxWidth?: number,
) =>
  SCALE_FACTORS.map((factor) => {
    const [url, width] = buildContentfulUrl(
      image,
      factor,
      queryParams,
      maxWidth,
    );
    return `${url} ${width}w`;
  }).join(',\n');

const LAYOUT_MAX_WIDTH: Record<Layout, number> = {
  fullWidth: 3200,
  constrained: 1600,
  fixed: 1200,
};

const DEFAULT_MAX_WIDTH = 1600;
const GIF_MAX_WIDTH = 750;

export const imageAssetToGatsbyImageData = (
  image: ImageAsset,
  layout?: Layout,
): IGatsbyImageData => {
  const isGif = image.mimeType === 'image/gif';
  const resolvedLayout = (layout ?? image.layout ?? 'constrained') as Layout;
  const layoutMaxWidth = LAYOUT_MAX_WIDTH[resolvedLayout] ?? DEFAULT_MAX_WIDTH;
  const targetMaxWidth = isGif
    ? Math.min(layoutMaxWidth, GIF_MAX_WIDTH)
    : layoutMaxWidth;
  const maxDisplayWidth = Math.min(image.width, targetMaxWidth);

  // For full-width hero style modules allow the browser to use the full viewport width
  // so large displays can request higher fidelity assets. Other layouts cap at their
  // expected rendered width to avoid oversized downloads.
  const sizes =
    resolvedLayout === 'fullWidth'
      ? '100vw'
      : `(min-width: ${maxDisplayWidth}px) ${maxDisplayWidth}px, 100vw`;

  const mimeToFm: Record<string, string> = {
    'image/jpeg': 'jpeg',
    'image/jpg': 'jpeg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/avif': 'avif',
  };
  const fmParam = mimeToFm[image.mimeType] ?? 'jpg';
  const fallbackQueryParams = {
    ...(image.mimeType === 'image/jpeg' && { fl: 'progressive' }),
    fm: fmParam,
  };

  // Don't generate WebP sources for GIFs (they should stay as GIFs for animation)
  const sources = isGif
    ? []
    : [
        {
          srcSet: buildSrcSet(image, { fm: 'webp' }, maxDisplayWidth),
          type: 'image/webp',
          sizes,
        },
      ];

  return {
    layout: layout ?? image.layout ?? 'constrained',
    width: image.width,
    height: image.height,
    images: {
      fallback: {
        src: buildContentfulUrl(
          image,
          1,
          fallbackQueryParams,
          maxDisplayWidth,
        )[0],
        srcSet: buildSrcSet(image, fallbackQueryParams, maxDisplayWidth),
        sizes,
      },
      sources,
    },
  };
};
